import { useState, type ElementType } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import GraficoLinha from '@/components/GraficoLinha';
import Preloader from '@/components/Preloader';
import { usePode } from '@/stores/authStore';
import { CELULA, LINHA } from '@/features/cotacoes/tabela';

/** O título de coluna que gruda: borda desenhada por sombra, porque a borda da tabela fica para trás ao rolar. */
const CABECALHO_FIXO = 'bg-surface-subtle px-sm py-sm font-bold shadow-[inset_-1px_-2px_0_var(--color-border)]';

type Valores = {
  cobre_usd: number | null;
  aluminio_usd: number | null;
  dolar: number | null;
  cobre_brl: number | null;
  aluminio_brl: number | null;
};

type Variacao = Record<keyof Valores, number | null>;

type Periodo = Valores & {
  rotulo: string;
  inicio: string;
  fim: string;
  pregoes: number;
  variacao: Variacao | null;
};

type Painel = {
  ultimo_pregao: string | null;
  capturado_em: string | null;
  desatualizado: boolean;
  hoje: (Valores & { data: string }) | null;
  variacoes: Record<'semana' | 'quinzena' | 'mes', (Variacao & { desde: string }) | null> | null;
  agrupamento: Agrupamento;
  periodos: Periodo[];
};

type Agrupamento = 'diario' | 'semanal' | 'quinzenal' | 'mensal';
type Serie = 'cobre' | 'aluminio' | 'dolar';
type Moeda = 'brl' | 'usd';

const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });
const reaisCurto = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const dolares = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const dolaresCurto = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const cotacaoDolar = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });
const dataCurta = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const horario = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const dia = (iso: string) => dataCurta.format(new Date(`${iso}T12:00:00`));

const AGRUPAMENTOS: { valor: Agrupamento; rotulo: string }[] = [
  { valor: 'diario', rotulo: 'Diário' },
  { valor: 'semanal', rotulo: 'Semanal' },
  { valor: 'quinzenal', rotulo: 'Quinzenal' },
  { valor: 'mensal', rotulo: 'Mensal' },
];

const PERIODOS = [3, 6, 12, 24];

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** O rótulo que cabe no eixo: "31/08", "1ª set/26", "set/26". */
function rotuloCurto(periodo: { inicio: string }, agrupamento: Agrupamento): string {
  const [ano, mes, diaDoMes] = periodo.inicio.split('-');
  const mesCurto = `${MESES[Number(mes) - 1]}/${ano.slice(2)}`;

  if (agrupamento === 'mensal') return mesCurto;
  if (agrupamento === 'quinzenal') return `${Number(diaDoMes) <= 15 ? '1ª' : '2ª'} ${mesCurto}`;

  return `${diaDoMes}/${mes}`;
}

/**
 * O "acompanhamento LME" do Marcelo, sem planilha: a tonelada de cobre e de
 * alumínio já em reais, o dólar do dia e a variação para a semana, quinzena ou
 * mês anterior — "se tu der um painelzinho que já vai me dar a tonelada do cobre
 * pronto, eu não vou mais acessar lá".
 *
 * Para quem compra, preço caindo é boa notícia: queda aparece em verde.
 */
export default function IndicesPage() {
  return (
    <div className="px-lg py-lg">
      <PainelIndices />
    </div>
  );
}

/**
 * O painel em si, sem a moldura da página: a mesma leitura aparece no menu e
 * no modal que a análise abre ("Ver o histórico do cobre"), onde o recorte
 * padrão é o diário dos últimos meses — o que importa perto das propostas.
 */
export function PainelIndices({
  agrupamentoInicial = 'mensal',
  mesesInicial = 12,
  Titulo = 'h1',
  emModal = false,
}: {
  agrupamentoInicial?: Agrupamento;
  mesesInicial?: number;
  Titulo?: ElementType;
  emModal?: boolean;
}) {
  const queryClient = useQueryClient();
  const podeAtualizar = usePode('indices.atualizar');
  const [agrupamento, setAgrupamento] = useState<Agrupamento>(agrupamentoInicial);
  const [meses, setMeses] = useState(mesesInicial);
  const [serie, setSerie] = useState<Serie>('cobre');
  const [moeda, setMoeda] = useState<Moeda>('brl');
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: painel, isLoading } = useQuery({
    queryKey: ['indices', agrupamento, meses],
    queryFn: async () =>
      (await api.get<{ data: Painel }>('/indices', { params: { agrupamento, meses } })).data.data,
  });

  const atualizar = useMutation({
    mutationFn: async () => (await api.post<{ message: string }>('/indices/atualizar')).data,
    onSuccess: (resposta) => {
      setAviso(resposta.message);
      queryClient.invalidateQueries({ queryKey: ['indices'] });
    },
    onError: () => setAviso('As fontes não responderam agora. Tente de novo em alguns minutos.'),
  });

  if (isLoading || !painel) {
    return (
      <>
        {/* No modal, o título precisa existir desde o primeiro quadro. */}
        <Titulo className="sr-only">Índice de metais LME</Titulo>
        {/* No modal, o carregamento fica dentro dele. */}
        <Preloader telaCheia={!emModal} mensagem="Carregando índices…" className="py-3xl" />
      </>
    );
  }

  const campo = (serie === 'dolar' ? 'dolar' : `${serie}_${moeda}`) as keyof Valores;
  const formatar = (v: number) =>
    serie === 'dolar' ? cotacaoDolar.format(v) : moeda === 'brl' ? reaisCurto.format(v) : dolaresCurto.format(v);
  const nomeSerie = serie === 'cobre' ? 'Cobre' : serie === 'aluminio' ? 'Alumínio' : 'Dólar PTAX';

  return (
    <div>
      <div className={`mb-md flex flex-wrap items-start justify-between gap-md ${emModal ? 'pr-10' : ''}`}>
        <div>
          <Titulo className="text-[18px] font-bold text-ink">Índice de metais LME</Titulo>
          <p className="text-[13px] text-muted">
            LME à vista (Westmetall) e dólar PTAX (Banco Central)
            {painel.ultimo_pregao ? ` · último pregão em ${dia(painel.ultimo_pregao)}` : ''}
            {painel.capturado_em ? ` · capturado em ${horario.format(new Date(painel.capturado_em))}` : ''}
          </p>
        </div>

        {podeAtualizar && (
          <button
            type="button"
            onClick={() => atualizar.mutate()}
            disabled={atualizar.isPending}
            className="inline-flex items-center gap-xs rounded-md border border-border bg-surface px-md py-sm text-[13px] font-bold text-ink hover:border-accent disabled:opacity-60"
          >
            <RefreshCw size={15} className={atualizar.isPending ? 'animate-spin' : ''} />
            {atualizar.isPending ? 'Atualizando…' : 'Atualizar agora'}
          </button>
        )}
      </div>

      {painel.desatualizado && (
        <p className="mb-md flex items-center gap-sm rounded-md border border-warning/40 bg-warning/5 px-md py-sm text-[13px] text-ink">
          <AlertTriangle size={15} className="shrink-0 text-warning" />
          O último pregão capturado tem mais de quatro dias. A captura automática pode ter parado.
        </p>
      )}

      {aviso && <p className="mb-md text-[13px] text-muted">{aviso}</p>}

      {painel.hoje && painel.variacoes && (
        <div className="mb-lg grid gap-md md:grid-cols-3">
          <CartaoIndice
            titulo="Cobre"
            principal={painel.hoje.cobre_brl !== null ? `${reais.format(painel.hoje.cobre_brl)}/t` : '—'}
            secundario={painel.hoje.cobre_usd !== null ? `${dolares.format(painel.hoje.cobre_usd)}/t na LME` : ''}
            variacoes={painel.variacoes}
            campo="cobre_brl"
          />
          <CartaoIndice
            titulo="Alumínio"
            principal={painel.hoje.aluminio_brl !== null ? `${reais.format(painel.hoje.aluminio_brl)}/t` : '—'}
            secundario={painel.hoje.aluminio_usd !== null ? `${dolares.format(painel.hoje.aluminio_usd)}/t na LME` : ''}
            variacoes={painel.variacoes}
            campo="aluminio_brl"
          />
          <CartaoIndice
            titulo="Dólar PTAX"
            principal={painel.hoje.dolar !== null ? cotacaoDolar.format(painel.hoje.dolar) : '—'}
            secundario="venda, Banco Central"
            variacoes={painel.variacoes}
            campo="dolar"
          />
        </div>
      )}

      <section className="mb-lg rounded-lg border border-border bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-sm border-b border-border px-md py-sm">
          <h2 className="text-[14px] font-bold text-ink">
            {nomeSerie}
            {serie !== 'dolar' ? (moeda === 'brl' ? ' em R$ por tonelada' : ' em US$ por tonelada') : ''}
          </h2>

          <div className="flex flex-wrap gap-sm">
            <Alternador
              rotulo="Índice"
              valor={serie}
              opcoes={[
                { valor: 'cobre', rotulo: 'Cobre' },
                { valor: 'aluminio', rotulo: 'Alumínio' },
                { valor: 'dolar', rotulo: 'Dólar' },
              ]}
              aoTrocar={setSerie}
            />
            {serie !== 'dolar' && (
              <Alternador
                rotulo="Moeda"
                valor={moeda}
                opcoes={[
                  { valor: 'brl', rotulo: 'R$/t' },
                  { valor: 'usd', rotulo: 'US$/t' },
                ]}
                aoTrocar={setMoeda}
              />
            )}
            <Alternador rotulo="Agrupamento" valor={agrupamento} opcoes={AGRUPAMENTOS} aoTrocar={setAgrupamento} />
            <Alternador
              rotulo="Período"
              valor={String(meses)}
              opcoes={PERIODOS.map((m) => ({ valor: String(m), rotulo: `${m} meses` }))}
              aoTrocar={(v) => setMeses(Number(v))}
            />
          </div>
        </div>

        <div className="px-sm py-md">
          <GraficoLinha
            descricao={`${nomeSerie}, ${agrupamento}, últimos ${meses} meses`}
            pontos={painel.periodos.map((p) => ({ rotulo: p.rotulo, rotuloCurto: rotuloCurto(p, agrupamento), valor: p[campo] }))}
            formatar={formatar}
          />
        </div>
      </section>

      {/* O cabeçalho gruda ao rolar: descendo sessenta pregões, continua claro
          qual número é de qual coluna. Grudar exige que a moldura não role de
          lado — no modal quem rola de lado, se precisar, é o próprio modal, e o
          cabeçalho sobe sobre o respiro do modal (p-lg) para não deixar uma
          fresta por onde as linhas aparecem; na
          página, a moldura rola de lado em tela estreita e aí o cabeçalho não
          gruda (grudaria dentro dela, deslocado sobre as linhas). As bordas do cabeçalho viram sombra, que acompanha a
          célula quando ela gruda. */}
      <section
        className={`rounded-lg border border-border bg-surface ${emModal ? '' : 'overflow-x-auto xl:overflow-visible'}`}
      >
        <table className="w-full text-[13px]">
          <thead
            className={`z-10 text-[11px] uppercase tracking-wide text-ink-soft ${emModal ? 'sticky top-[calc(-1*var(--spacing-lg))]' : 'xl:sticky xl:top-[var(--altura-barra,54px)]'}`}
          >
            <tr>
              <th className={`${CABECALHO_FIXO} rounded-tl-lg text-left`}>{agrupamento === 'diario' ? 'Pregão' : 'Período (média)'}</th>
              <th className={`${CABECALHO_FIXO} text-right`}>Cobre US$/t</th>
              <th className={`${CABECALHO_FIXO} text-right`}>Cobre R$/t</th>
              <th className={`${CABECALHO_FIXO} text-right`}>Var.</th>
              <th className={`${CABECALHO_FIXO} text-right`}>Alumínio US$/t</th>
              <th className={`${CABECALHO_FIXO} text-right`}>Alumínio R$/t</th>
              <th className={`${CABECALHO_FIXO} text-right`}>Var.</th>
              <th className={`${CABECALHO_FIXO} text-right`}>Dólar</th>
              <th className={`${CABECALHO_FIXO} rounded-tr-lg text-right shadow-[inset_0_-2px_0_var(--color-border)]`}>Var.</th>
            </tr>
          </thead>
          <tbody>
            {[...painel.periodos].reverse().map((p) => (
              <tr key={p.inicio + p.rotulo} className={LINHA}>
                <td className={CELULA}>
                  <span className="text-ink">{p.rotulo}</span>
                  {agrupamento !== 'diario' && (
                    <span className="block text-[11px] text-muted">
                      {p.pregoes} {p.pregoes === 1 ? 'pregão' : 'pregões'}
                    </span>
                  )}
                </td>
                <td className={`${CELULA} text-right tabular-nums`}>{p.cobre_usd !== null ? dolares.format(p.cobre_usd) : '—'}</td>
                <td className={`${CELULA} text-right font-bold tabular-nums text-ink`}>{p.cobre_brl !== null ? reais.format(p.cobre_brl) : '—'}</td>
                <td className={`${CELULA} text-right`}><Variacao valor={p.variacao?.cobre_brl ?? null} /></td>
                <td className={`${CELULA} text-right tabular-nums`}>{p.aluminio_usd !== null ? dolares.format(p.aluminio_usd) : '—'}</td>
                <td className={`${CELULA} text-right font-bold tabular-nums text-ink`}>{p.aluminio_brl !== null ? reais.format(p.aluminio_brl) : '—'}</td>
                <td className={`${CELULA} text-right`}><Variacao valor={p.variacao?.aluminio_brl ?? null} /></td>
                <td className={`${CELULA} text-right tabular-nums`}>{p.dolar !== null ? cotacaoDolar.format(p.dolar) : '—'}</td>
                <td className={`${CELULA} text-right`}><Variacao valor={p.variacao?.dolar ?? null} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className="mt-sm text-[12px] text-muted">
        Tonelada em reais = LME à vista × dólar PTAX do mesmo dia (ou do último dia útil antes dele, quando os feriados
        de Londres e do Brasil não coincidem).
      </p>
    </div>
  );
}

function CartaoIndice({
  titulo,
  principal,
  secundario,
  variacoes,
  campo,
}: {
  titulo: string;
  principal: string;
  secundario: string;
  variacoes: NonNullable<Painel['variacoes']>;
  campo: keyof Valores;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <p className="bg-faixa px-md py-xs text-[12px] font-bold uppercase tracking-wide text-white">{titulo}</p>
      <div className="px-md py-md">
        <p className="text-[22px] font-bold tabular-nums text-ink">{principal}</p>
        <p className="text-[12px] text-muted">{secundario}</p>
        <dl className="mt-sm grid grid-cols-3 gap-xs border-t border-border pt-sm">
          {(['semana', 'quinzena', 'mes'] as const).map((janela) => (
            <div key={janela}>
              <dt className="text-[10px] uppercase tracking-wide text-muted">{janela === 'mes' ? 'Mês' : janela === 'semana' ? 'Semana' : 'Quinzena'}</dt>
              <dd>
                <Variacao valor={variacoes[janela]?.[campo] ?? null} />
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

/** Para quem compra, queda é favorável: verde para baixo, vermelho para cima. */
function Variacao({ valor }: { valor: number | null }) {
  if (valor === null) return <span className="text-[12px] text-muted">—</span>;

  const pct = `${valor > 0 ? '+' : ''}${(valor * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  const Icone = valor < 0 ? ArrowDownRight : ArrowUpRight;

  return (
    <span className={`inline-flex items-center gap-[2px] text-[12px] font-bold tabular-nums ${valor < 0 ? 'text-success' : valor > 0 ? 'text-danger' : 'text-muted'}`}>
      {valor !== 0 && <Icone size={13} aria-hidden="true" />}
      {pct}
    </span>
  );
}

function Alternador<T extends string>({
  rotulo,
  valor,
  opcoes,
  aoTrocar,
}: {
  rotulo: string;
  valor: T;
  opcoes: { valor: T; rotulo: string }[];
  aoTrocar: (valor: T) => void;
}) {
  return (
    <div role="group" aria-label={rotulo} className="inline-flex gap-[2px] rounded-md bg-surface-subtle p-[3px]">
      {opcoes.map((opcao) => (
        <button
          key={opcao.valor}
          type="button"
          aria-pressed={opcao.valor === valor}
          onClick={() => aoTrocar(opcao.valor)}
          className={[
            'rounded px-sm py-[3px] text-[12px] transition-colors',
            opcao.valor === valor ? 'bg-surface font-bold text-ink shadow-sm' : 'text-muted hover:text-ink',
          ].join(' ')}
        >
          {opcao.rotulo}
        </button>
      ))}
    </div>
  );
}
